import React, { useState } from 'react';
import { ArrowLeft, CheckCircle2, Circle, ChevronRight, Trophy, Lock, Download } from 'lucide-react';
import { MODUL_LABEL, BOBOT_MODUL } from './progressTracker';
import { unduhPdf } from './unduhPdf';
import TripayPembayaran from './TripayPembayaran';
import DuitkuPembayaran from './DuitkuPembayaran';
import TransferManualPembayaran from './TransferManualPembayaran';
import { modulBaruAktif, MODUL_BARU_IDS } from './modulBaruConfig';

const API_URL = 'https://skortes.com';

// ─── Kontrol tampilan payment gateway ────────────────────────────
// Sengaja dibuat flag on/off (bukan hapus kode) karena belum tahu provider mana
// yang akan disetujui Production duluan (Midtrans/Tripay/Duitku). Kodenya tetap
// utuh di file masing-masing — tinggal balik flag ini ke `true` kapan saja untuk
// menampilkan opsi itu lagi ke user, tanpa perlu coding ulang.
const TAMPILKAN_MIDTRANS = true;
const TAMPILKAN_TRIPAY = false;
const TAMPILKAN_DUITKU = false;
// Transfer Manual dimatikan — Midtrans sudah production & aktif (akun lolos
// review 30 Jul 2026), jadi fallback manual (yang butuh verifikasi tangan admin
// per klaim, rawan human-error) sudah tidak diperlukan lagi. Endpoint backend-nya
// juga ditutup (lihat klaim-transfer-manual di server), kode UI-nya tetap utuh
// kalau nanti perlu diaktifkan lagi.
const TAMPILKAN_TRANSFER_MANUAL = false;

export const MODUL_DASAR = ['kraepelin', 'kecermatan', 'logika', 'verbal', 'figural', 'kepribadian', 'akademik', 'armyalpha', 'mekanik', 'substitusi', 'kestabilan'];

// Roster modul resmi Tryout — dinamis berdasar tanggal_mulai tryout, BUKAN
// konstanta statis lagi. Modul baru (Interpretasi Gambar & Interpretasi
// Bentuk) ditambahkan di tengah minggu berjalan, jadi supaya tidak
// mengganggu peserta yang sedang/baru mengerjakan Tryout minggu ini, modul
// baru itu HANYA ikut roster resmi mulai tryout yang tanggal_mulai-nya
// >= tanggal di modulBaruConfig.js. Sebelum itu, roster tetap 11 modul lama.
export function getUrutanModul(tryout) {
  return modulBaruAktif(tryout?.tanggal_mulai) ? [...MODUL_DASAR, ...MODUL_BARU_IDS] : MODUL_DASAR;
}
// Tetap diekspor sebagai daftar LENGKAP (dipakai tempat yang butuh "semua
// modul yang mungkin ada", bukan spesifik roster satu tryout tertentu).
export const URUTAN_MODUL = [...MODUL_DASAR, ...MODUL_BARU_IDS];

function formatTgl(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'][d.getMonth()]} ${d.getFullYear()}`;
}

function formatRupiah(n) {
  return 'Rp' + Number(n || 0).toLocaleString('id-ID');
}

export default function TryoutScreen({ tryout, modulSelesai, onMulaiModul, onSubmit, onTutup, sudahDikerjakan, hasilAkhir, loadingSubmit, perluBayar, sudahBayar, onBayar, loadingBayar }) {
  const urutanModul = getUrutanModul(tryout);
  const totalSelesai = Object.keys(modulSelesai).length;
  const semuaSelesai = totalSelesai === urutanModul.length;
  const terkunci = !!perluBayar && !sudahBayar; // belum bayar → checklist dikunci
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [errorPdf, setErrorPdf] = useState('');
  const [metodeLain, setMetodeLain] = useState(null); // null | 'tripay' | 'duitku' | 'manual'

  const handleUnduhPdfTryout = async () => {
    setLoadingPdf(true);
    setErrorPdf('');
    try {
      const token = localStorage.getItem('token');
      await unduhPdf(`${API_URL}/api/laporan/tryout/${tryout.id}`, token, 'laporan-tryout.pdf');
    } catch (err) {
      setErrorPdf(err.message || 'Gagal mengunduh laporan.');
    }
    setLoadingPdf(false);
  };

  // ── Layar hasil (kalau tryout sudah pernah disubmit) ──────────
  if (sudahDikerjakan && hasilAkhir) {
    const skorPerModul = hasilAkhir.skor_per_modul || {};
    return (
      <div className="px-5 pt-6 pb-6 space-y-5">
        <button onClick={onTutup} className="flex items-center gap-2 text-stone-400 text-sm">
          <ArrowLeft size={16} /> Kembali
        </button>
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--bg-card3)', border: '2px solid var(--accent)' }}>
            <Trophy size={28} style={{ color: 'var(--accent)' }} />
          </div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Skor akumulasi tryout</p>
          <p className="text-4xl font-extrabold font-mono" style={{ color: 'var(--accent)' }}>{hasilAkhir.skor_akumulasi}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Diselesaikan {formatTgl(hasilAkhir.selesai_at)}</p>
        </div>

        <button onClick={handleUnduhPdfTryout} disabled={loadingPdf}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm"
          style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--accent)', color: 'var(--accent)' }}>
          <Download size={16} /> {loadingPdf ? 'Menyiapkan PDF...' : 'Unduh Laporan PDF'}
        </button>
        {errorPdf && <p className="text-xs text-center" style={{ color: 'var(--danger, #f87171)' }}>{errorPdf}</p>}

        <div className="space-y-2">
          {Object.entries(skorPerModul).map(([modulId, skor]) => (
            <div key={modulId} className="flex items-center justify-between rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                {MODUL_LABEL[modulId]?.icon || '📝'} {MODUL_LABEL[modulId]?.nama || modulId}
              </span>
              <span className="font-bold font-mono" style={{ color: 'var(--accent)' }}>{skor}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-center" style={{ color: 'var(--text-faint)' }}>Kamu hanya bisa mengerjakan tryout ini satu kali.</p>
      </div>
    );
  }

  // ── Layar checklist (sedang mengerjakan) ──────────────────────
  return (
    <div className="px-5 pt-6 pb-6 space-y-5">
      <button onClick={onTutup} className="flex items-center gap-2 text-stone-400 text-sm">
        <ArrowLeft size={16} /> Kembali
      </button>

      <div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Tryout resmi — dikerjakan berurutan</p>
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>{tryout.judul}</h2>
        {tryout.deskripsi && <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{tryout.deskripsi}</p>}
        <p className="text-xs mt-2" style={{ color: 'var(--text-faint)' }}>Berakhir {formatTgl(tryout.tanggal_selesai)}</p>
      </div>

      {/* ── Paywall: tampil kalau tryout berbayar dan belum dibayar ── */}
      {terkunci && (
        <div className="rounded-xl p-5 text-center space-y-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--accent)' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'var(--bg-card3)' }}>
            <Lock size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>Tryout ini berbayar</p>
          <p className="text-2xl font-extrabold font-mono" style={{ color: 'var(--accent)' }}>{formatRupiah(tryout.harga)}</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Selesaikan pembayaran untuk membuka semua modul tryout.</p>

          {TAMPILKAN_MIDTRANS && (
            <button onClick={onBayar} disabled={loadingBayar}
              className="w-full py-3 rounded-lg font-bold text-sm uppercase tracking-wide mt-2"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
              {loadingBayar ? 'Menyiapkan pembayaran...' : `Bayar ${formatRupiah(tryout.harga)}`}
            </button>
          )}

          {/* Kalau Midtrans dimatikan, provider alternatif jadi opsi UTAMA (bukan "metode lain") */}
          {metodeLain === null && (TAMPILKAN_TRIPAY || TAMPILKAN_DUITKU || TAMPILKAN_TRANSFER_MANUAL) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {TAMPILKAN_TRIPAY && (
                <button onClick={() => setMetodeLain('tripay')}
                  className={TAMPILKAN_MIDTRANS ? "flex-1 py-2 rounded-lg text-xs font-medium" : "flex-1 py-3 rounded-lg font-bold text-sm uppercase tracking-wide"}
                  style={TAMPILKAN_MIDTRANS
                    ? { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }
                    : { backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
                  {TAMPILKAN_MIDTRANS ? 'Metode lain: Tripay' : `Bayar ${formatRupiah(tryout.harga)}`}
                </button>
              )}
              {TAMPILKAN_DUITKU && (
                <button onClick={() => setMetodeLain('duitku')}
                  className={TAMPILKAN_MIDTRANS ? "flex-1 py-2 rounded-lg text-xs font-medium" : "flex-1 py-3 rounded-lg font-bold text-sm uppercase tracking-wide"}
                  style={TAMPILKAN_MIDTRANS
                    ? { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }
                    : { backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
                  {TAMPILKAN_MIDTRANS ? 'Metode lain: Duitku' : `Bayar ${formatRupiah(tryout.harga)}`}
                </button>
              )}
              {TAMPILKAN_TRANSFER_MANUAL && (
                <button onClick={() => setMetodeLain('manual')}
                  className={TAMPILKAN_MIDTRANS ? "flex-1 py-2 rounded-lg text-xs font-medium" : "flex-1 py-3 rounded-lg font-bold text-sm uppercase tracking-wide"}
                  style={TAMPILKAN_MIDTRANS
                    ? { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }
                    : { backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif" }}>
                  {TAMPILKAN_MIDTRANS ? 'Metode lain: Transfer Manual' : `Bayar ${formatRupiah(tryout.harga)} (Transfer Manual)`}
                </button>
              )}
            </div>
          )}
          {metodeLain === 'tripay' && TAMPILKAN_TRIPAY && (
            <TripayPembayaran
              tryoutId={tryout.id}
              onSukses={() => window.location.reload()}
              onTutup={() => setMetodeLain(null)}
            />
          )}
          {metodeLain === 'duitku' && TAMPILKAN_DUITKU && (
            <DuitkuPembayaran
              tryoutId={tryout.id}
              onSukses={() => window.location.reload()}
              onTutup={() => setMetodeLain(null)}
            />
          )}
          {metodeLain === 'manual' && TAMPILKAN_TRANSFER_MANUAL && (
            <TransferManualPembayaran
              tryoutId={tryout.id}
              harga={tryout.harga}
              onSukses={() => window.location.reload()}
              onTutup={() => setMetodeLain(null)}
            />
          )}
        </div>
      )}

      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
          <span>Progress</span>
          <span style={{ color: 'var(--text-primary)' }}>{totalSelesai} / {urutanModul.length} modul</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-app)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${(totalSelesai / urutanModul.length) * 100}%`, backgroundColor: 'var(--accent)' }} />
        </div>
      </div>

      <div className="space-y-2" style={{ opacity: terkunci ? 0.5 : 1, pointerEvents: terkunci ? 'none' : 'auto' }}>
        {urutanModul.map(modulId => {
          const selesai = modulSelesai[modulId] != null;
          const info = MODUL_LABEL[modulId] || { nama: modulId, icon: '📝' };
          return (
            <button key={modulId} onClick={() => !selesai && !terkunci && onMulaiModul(modulId)} disabled={selesai || terkunci}
              className="w-full flex items-center gap-3 rounded-xl p-4 text-left"
              style={{ backgroundColor: 'var(--bg-card2)', border: `1px solid ${selesai ? 'var(--success)' : 'var(--border)'}`, opacity: selesai ? 0.85 : 1 }}>
              {selesai ? <CheckCircle2 size={20} style={{ color: 'var(--success)' }} /> : <Circle size={20} style={{ color: 'var(--text-faint)' }} />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{info.icon} {info.nama}</p>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Bobot {BOBOT_MODUL[modulId] || 1}x</p>
              </div>
              {selesai ? (
                <span className="font-bold font-mono text-sm" style={{ color: 'var(--success)' }}>{modulSelesai[modulId]}</span>
              ) : (
                <ChevronRight size={18} style={{ color: 'var(--text-faint)' }} />
              )}
            </button>
          );
        })}
      </div>

      <button onClick={onSubmit} disabled={!semuaSelesai || loadingSubmit || terkunci}
        className="w-full py-3.5 rounded-lg font-bold text-sm uppercase tracking-wide"
        style={{
          backgroundColor: semuaSelesai && !terkunci ? 'var(--accent)' : 'var(--border)',
          color: semuaSelesai && !terkunci ? 'var(--bg-app)' : 'var(--text-faint)',
          fontFamily: "'Poppins', sans-serif",
          cursor: semuaSelesai && !terkunci ? 'pointer' : 'not-allowed',
        }}>
        {loadingSubmit ? 'Mengirim...' : semuaSelesai ? 'Kirim Hasil Tryout →' : `Selesaikan semua modul dulu (${totalSelesai}/${urutanModul.length})`}
      </button>
    </div>
  );
}
